/** @format */

import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { BiLogInCircle } from "react-icons/bi";
import { BsChat, BsHeart } from "react-icons/bs";
import { FaBars, FaTimes, FaUserLock } from "react-icons/fa";
import { RiProductHuntLine, RiStoreLine } from "react-icons/ri";
import { RxDashboard } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import FadeLoader from "react-spinners/FadeLoader";
import api from "../api/api";
import Headers from "../components/Headers";
import { logout } from "../store/reducers/authReducer";
import { useCategories } from "../utils/Categories";

// ─── Nav items config ────────────────────────────────────────────────────────
const getNavItems = (t, handleSwitchToSeller, dispatch) => [
  { icon: RxDashboard, label: t("Dashboard"), to: "/dashboard" },
  {
    icon: RiProductHuntLine,
    label: t("My Orders"),
    to: "/dashboard/my_orders",
  },
  { icon: BsHeart, label: t("My Wishlist"), to: "/dashboard/my_wishlist" },
  { icon: BsChat, label: t("Chats"), to: "/dashboard/my_chats" },
  {
    icon: FaUserLock,
    label: t("Change Password"),
    to: "/dashboard/change_password",
  },
  {
    icon: RiStoreLine,
    label: t("Your Sale Board"),
    action: handleSwitchToSeller,
  },
  {
    icon: BiLogInCircle,
    label: t("Logout"),
    action: () => dispatch(logout()),
    danger: true,
  },
];

// ─── Sidebar item ────────────────────────────────────────────────────────────
const SidebarItem = ({ icon: Icon, label, to, action, danger, onClose }) => {
  const baseClass =
    "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 w-full text-left";

  if (to) {
    return (
      <NavLink
        to={to}
        end
        onClick={onClose}
        className={({ isActive }) =>
          `${baseClass} ${
            isActive
              ? "bg-blue-600 text-white shadow-sm shadow-blue-200"
              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          }`
        }
      >
        <Icon className='text-[17px] flex-shrink-0' />
        <span>{label}</span>
      </NavLink>
    );
  }

  return (
    <button
      onClick={() => {
        action?.();
        onClose?.();
      }}
      className={`${baseClass} ${
        danger
          ? "text-red-500 hover:bg-red-50 hover:text-red-600"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <Icon className='text-[17px] flex-shrink-0' />
      <span>{label}</span>
    </button>
  );
};

// ─── Sidebar ─────────────────────────────────────────────────────────────────
const Sidebar = ({ open, onClose, navItems }) => (
  <>
    {/* Backdrop mobile */}
    {open && (
      <div
        className='fixed inset-0 z-40 bg-black/30 backdrop-blur-sm md-lg:block hidden'
        onClick={onClose}
      />
    )}

    <aside
      className={`
        flex-shrink-0 w-[240px] flex flex-col bg-white border-r border-slate-100
        md-lg:fixed md-lg:top-0 md-lg:left-0 md-lg:h-full md-lg:z-50 md-lg:shadow-xl
        transition-transform duration-300
        ${open ? "md-lg:translate-x-0" : "md-lg:-translate-x-full"}
      `}
    >
      {/* Sidebar header */}
      <div className='flex items-center justify-between px-4 py-5 border-b border-slate-100'>
        <div className='flex items-center gap-2'>
          <div className='w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center'>
            <RxDashboard className='text-white text-sm' />
          </div>
          <span className='font-bold text-slate-800 text-sm tracking-tight'>
            Mon Espace
          </span>
        </div>
        <button
          onClick={onClose}
          className='hidden md-lg:flex w-7 h-7 items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400'
        >
          <FaTimes size={12} />
        </button>
      </div>

      {/* Nav links */}
      <nav className='flex-1 overflow-y-auto px-3 py-4 space-y-0.5'>
        {navItems.map((item, i) => (
          <SidebarItem key={i} {...item} onClose={onClose} />
        ))}
      </nav>

      {/* Footer */}
      <div className='px-4 py-4 border-t border-slate-100'>
        <p className='text-[11px] text-slate-400 text-center'>
          © 2025 GobyMall
        </p>
      </div>
    </aside>
  </>
);

// ─── Loading overlay ──────────────────────────────────────────────────────────
const LoadingOverlay = () => (
  <div className='fixed inset-0 z-[99999] bg-white/70 backdrop-blur-sm flex items-center justify-center'>
    <FadeLoader color='#2563EB' />
  </div>
);

// ─── Dashboard ────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { categories, loading, errorMessage, clientLocation } = useCategories();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { customerInfo } = useSelector((s) => s.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleSwitchToSeller = async () => {
    try {
      navigate("/");
      const { data } = await api.get(
        `/api/customer/switch-to-seller/${customerInfo.id}`,
        {
          withCredentials: true,
        },
      );
      await dispatch(logout());
      window.location.href = `${data.redirect}?from=customer`;
    } catch {
      toast(t("You dont have seller account"));
    }
  };

  const navItems = getNavItems(t, handleSwitchToSeller, dispatch);

  return (
    <div className='min-h-screen bg-slate-50 flex flex-col'>
      <Headers categories={categories} clientLocation={clientLocation} />

      {loading && <LoadingOverlay />}

      {errorMessage && (
        <div className='w-[95%] mx-auto mt-4'>
          <div className='bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm'>
            {t("Error")}: {errorMessage}
          </div>
        </div>
      )}

      {/* Mobile toggle button */}
      <div className='hidden md-lg:flex items-center px-4 pt-4'>
        <button
          onClick={() => setSidebarOpen(true)}
          className='flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium shadow-sm shadow-blue-200 hover:bg-blue-700 transition-colors'
        >
          <FaBars size={14} />
          <span>Menu</span>
        </button>
      </div>

      {/* Main layout */}
      <div className='flex flex-1 w-[95%] mx-auto py-6 gap-5 md-lg:w-full md-lg:px-4 md-lg:py-4'>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          navItems={navItems}
        />

        {/* Content area */}
        <main className='flex-1 min-w-0'>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
